import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ScalesIcon } from '@phosphor-icons/react';
import { Button, EmptyState } from '@origo/origo-ui';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import DispensationGroupHeader from './components/DispensationGroupHeader';
import DispensationListItem from './components/DispensationListItem';
import NewDispensationDialog from './components/NewDispensationDialog';
import type { DispensationFieldErrors } from './components/DispensationForm';
import type { DispensationGroup, DispensasjonFormValues, FieldChangeHandler } from '../../types';
import styles from './DispensationsPage.module.css';

export interface NewDispensationState {
    open: boolean;
    values: DispensasjonFormValues;
    fieldErrors: DispensationFieldErrors;
    isSaving: boolean;
}

interface DispensationsPageProps {
    groups: DispensationGroup[];
    openGroups: Set<DispensationGroup['key']>;
    onToggleGroup: (key: DispensationGroup['key']) => void;

    newDispensation: NewDispensationState;
    onNewOpen: () => void;
    onNewClose: () => void;
    onNewChange: FieldChangeHandler<DispensasjonFormValues>;
    onNewSave: () => void;
}

interface DispensationGroupListProps {
    groups: DispensationGroup[];
    openGroups: Set<DispensationGroup['key']>;
    onToggleGroup: (key: DispensationGroup['key']) => void;
    t: TFunction;
}

/** Memoized slik at tastetrykk i "ny dispensasjon"-dialogen ikke rerendrer hele listen. */
const DispensationGroupList: React.FC<DispensationGroupListProps> = React.memo(
    ({ groups, openGroups, onToggleGroup, t }) => (
        <>
            {groups.map(group => (
                <DispensationGroupHeader
                    key={group.key}
                    label={t(`dispensations.groups.${group.key}`)}
                    count={group.count}
                    open={openGroups.has(group.key)}
                    onToggle={() => onToggleGroup(group.key)}
                >
                    {group.items.length === 0 ? (
                        <p className={styles.empty}>{t('dispensations.emptyGroup')}</p>
                    ) : (
                        <ul className={styles.list}>
                            {group.items.map(item => (
                                <DispensationListItem key={item.id} item={item} />
                            ))}
                        </ul>
                    )}
                </DispensationGroupHeader>
            ))}
        </>
    )
);

const DispensationsPage: React.FC<DispensationsPageProps> = ({
    groups,
    openGroups,
    onToggleGroup,
    newDispensation,
    onNewOpen,
    onNewClose,
    onNewChange,
    onNewSave,
}) => {
    const { t } = useTranslation();

    const isEmpty = groups.length === 0 || groups.every(g => g.items.length === 0);

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('dispensations.title')}
                subtitle={t('dispensations.subtitle')}
                actions={
                    !isEmpty ? (
                        <Button variant="secondary" size="md" onClick={onNewOpen}>
                            {t('dispensations.newDispensation')}
                        </Button>
                    ) : undefined
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<ScalesIcon size={32} weight="duotone" />}
                        title={t('dispensations.empty.title')}
                        description={t('dispensations.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={onNewOpen}>
                                {t('dispensations.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <DispensationGroupList
                        groups={groups}
                        openGroups={openGroups}
                        onToggleGroup={onToggleGroup}
                        t={t}
                    />
                )}
            </div>
            <NewDispensationDialog
                {...newDispensation}
                onChange={onNewChange}
                onClose={onNewClose}
                onSave={onNewSave}
            />
        </article>
    );
};

export default DispensationsPage;
